/**
 * RegulatoryFeedTab - TASK-254
 * Regulatory RSS feed ingestion and AI rule extraction
 * Ported from prototypes/aml-sentinel/components/RegulatoryFeed.tsx
 */

import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  RefreshCw,
  Loader2,
  Globe,
  Calendar,
  ExternalLink,
  Rss,
  Shield,
  CheckSquare,
  X,
} from 'lucide-react';
import {
  listRegulations,
  fetchRSSFeed,
  listRSSSources,
  createRegulation,
  extractRules,
  AMLRiskLevel,
  Regulation,
  MonitoringRule,
} from '../../services/amlService';

export const RegulatoryFeedTab: React.FC = () => {
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [rssSources, setRssSources] = useState<{ id: string; name: string; url: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [showInput, setShowInput] = useState(false);
  const [newRegText, setNewRegText] = useState('');
  const [newRegTitle, setNewRegTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadRegulations();
    loadRSSSources();
  }, []);

  const loadRegulations = async () => {
    try {
      setLoading(true);
      const response = await listRegulations({ limit: 50 });
      setRegulations(response.regulations);
    } catch (e) {
      console.error('Failed to load regulations:', e);
      setError('Failed to load regulations');
    } finally {
      setLoading(false);
    }
  };

  const loadRSSSources = async () => {
    try {
      const response = await listRSSSources();
      setRssSources(response.sources);
      if (response.sources.length > 0) {
        setSelectedSource(response.sources[0].id);
      }
    } catch (e) {
      console.error('Failed to load RSS sources:', e);
    }
  };

  const handleFetchRSS = async () => {
    if (!selectedSource) return;
    setIsFetching(true);
    setError(null);

    try {
      const result = await fetchRSSFeed(selectedSource, true);
      if (result.success) {
        // Reload regulations to show new entries
        await loadRegulations();
      }
    } catch (e) {
      console.error('RSS fetch failed:', e);
      setError('Failed to fetch RSS feed. Check backend service.');
    } finally {
      setIsFetching(false);
    }
  };

  const handleManualIngest = async () => {
    if (!newRegText.trim()) return;
    setIsIngesting(true);
    setError(null);

    try {
      // First extract rules from the text
      const extracted = await extractRules(newRegText);

      // Then create the regulation
      const result = await createRegulation({
        title: newRegTitle || extracted.title || 'Manual Regulation',
        summary: extracted.summary,
        raw_content: newRegText,
        source: 'Manual Upload',
        impact_level: extracted.impactLevel,
      });

      if (result.success) {
        // Add to list
        setRegulations((prev) => [result.regulation, ...prev]);
        // Reset form
        setNewRegText('');
        setNewRegTitle('');
        setShowInput(false);
      }
    } catch (e) {
      console.error('Manual ingestion failed:', e);
      setError('Failed to process regulation. Check backend service.');
    } finally {
      setIsIngesting(false);
    }
  };

  const getImpactColor = (level: AMLRiskLevel | string) => {
    switch (level) {
      case 'Critical':
        return 'bg-red-800 text-white';
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-orange-100 text-orange-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getRuleIndicatorColor = (severity: AMLRiskLevel | string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-600';
      case 'High':
        return 'bg-orange-500';
      default:
        return 'bg-green-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Regulatory Ingestion Engine</h3>
          <p className="text-gray-500 mt-1">
            Live monitoring of regulatory RSS feeds and AI rule extraction.
          </p>
        </div>
        <div className="flex gap-3">
          {/* RSS Source Selector */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {rssSources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>

          <button
            onClick={handleFetchRSS}
            disabled={isFetching || !selectedSource}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetching ? <Loader2 className="animate-spin" size={18} /> : <Rss size={18} />}
            {isFetching ? 'Fetching...' : 'Fetch RSS'}
          </button>

          <button
            onClick={() => setShowInput(!showInput)}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            {showInput ? <X size={18} /> : <Plus size={18} />}
            {showInput ? 'Cancel' : 'Manual Ingest'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Manual Input Form */}
      {showInput && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in slide-in-from-top-4">
          <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Shield size={20} className="text-blue-600" /> Create New Monitoring Rules
          </h4>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regulation Title
              </label>
              <input
                type="text"
                value={newRegTitle}
                onChange={(e) => setNewRegTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700 text-sm"
                placeholder="e.g., FINMA Circular 2024/1 - Transaction Monitoring"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paste Circular Text / Requirements
              </label>
              <textarea
                value={newRegText}
                onChange={(e) => setNewRegText(e.target.value)}
                className="w-full h-40 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-gray-700 text-sm"
                placeholder="e.g., The Monetary Authority requires immediate blocking of transactions related to..."
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleManualIngest}
                disabled={isIngesting || !newRegText.trim()}
                className="bg-gray-900 text-white px-6 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {isIngesting ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <RefreshCw size={18} />
                )}
                {isIngesting ? 'Extracting Rules...' : 'Ingest & Generate Rules'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regulations List */}
      <div className="space-y-4">
        {regulations.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Regulations Yet</h3>
            <p className="text-gray-600 mb-4">
              Fetch from an RSS feed or manually ingest regulation text to get started.
            </p>
          </div>
        ) : (
          regulations.map((reg) => (
            <div
              key={reg.id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow relative overflow-hidden group"
            >
              {/* Source Indicator */}
              {reg.source.includes('RSS') && (
                <div className="absolute top-0 right-0 bg-orange-500 text-white text-[10px] px-2 py-0.5 rounded-bl-lg font-bold flex items-center gap-1">
                  <Rss size={10} /> LIVE FEED
                </div>
              )}
              {reg.source === 'Manual Upload' && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-bl-lg font-bold flex items-center gap-1">
                  <Shield size={10} /> AI ANALYZED
                </div>
              )}

              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getImpactColor(reg.impactLevel)}`}
                  >
                    {reg.impactLevel} Impact
                  </span>
                  <span className="text-xs text-gray-400 font-mono">{reg.id}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm gap-4">
                  <div className="flex items-center gap-1 font-medium text-gray-500">
                    <Globe size={14} /> {reg.source}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar size={14} /> {reg.date}
                  </div>
                </div>
              </div>

              <h4 className="text-lg font-semibold text-gray-800 mb-2 pr-12">{reg.title}</h4>
              <p className="text-gray-600 leading-relaxed text-sm mb-4">{reg.summary}</p>

              {/* Display Extracted Rules */}
              {reg.extractedRules && reg.extractedRules.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center gap-2">
                    <CheckSquare size={14} className="text-gray-500" />
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Active Monitoring Rules
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {reg.extractedRules.map((rule: MonitoringRule, idx: number) => (
                      <div key={idx} className="p-3 flex items-start gap-3">
                        <div
                          className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${getRuleIndicatorColor(rule.riskSeverity)}`}
                        />
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-gray-800">{rule.ruleName}</span>
                            <span className="text-[10px] text-gray-400 px-1 border rounded">
                              {rule.id}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">{rule.criteria}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw Content Preview if no rules */}
              {(!reg.extractedRules || reg.extractedRules.length === 0) && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mb-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Key Content
                  </div>
                  <p className="text-xs font-mono text-gray-600 line-clamp-2">{reg.rawContent}</p>
                </div>
              )}

              {/* Links */}
              {reg.links && reg.links.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-400 flex items-center">Sources:</span>
                  {reg.links.map((link, i) => (
                    <a
                      key={i}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded border border-blue-100 transition-colors hover:bg-blue-100"
                    >
                      <ExternalLink size={10} />
                      Read Official Document
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RegulatoryFeedTab;
